import React, { useMemo, useState } from "react";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { getClientTypeLabel, RoleName } from "constants/index";
import {
  useClientCharacteristicsSelector,
  useClientIdSelector,
  useClientSelector,
  usePickUserPermissions,
  useUserRoleSelector
} from "store";
import { UserPermissionType } from "store/types";
import { IClientDetailCharacteristic } from "store/types/client";
import { ClientType } from "store/types/user-management";

import { ClientCharacteristicPill } from "./ClientCharacteristicPill";
import { ClientCharacteristicsDrawer } from "./ClientCharacteristicsDrawer";

/** Display value for a characteristic: joined data value(s) with optional currency, or an em-dash when unset. */
const displayValue = (item: IClientDetailCharacteristic): string => {
  const values = (item.data ?? [])
    .map((entry) => {
      if (!entry.value) return null;
      // The base "Type" characteristic stores a raw ClientType key — show our human-readable label.
      const value = item.name === "Type" ? (getClientTypeLabel(entry.value as ClientType) ?? entry.value) : entry.value;
      return entry.currency ? `${entry.currency} ${value}` : value;
    })
    .filter(Boolean) as string[];
  return values.length ? values.join(", ") : "N/A";
};

/**
 * Read-only characteristics bar on the client card. Values come inline from the
 * GET /clients/{id} response (no separate request). The collapsed bar shows the base
 * (is_system) characteristics as pills; expanding lists all of them.
 */
export const ClientCharacteristicValues: React.FC = () => {
  const characteristics = useClientCharacteristicsSelector();
  const clientId = useClientIdSelector();
  const role = useUserRoleSelector();

  // Characteristics permission (global user permission), mirroring the deal CharacteristicsBar.
  const myCharacteristics = usePickUserPermissions("characteristics");
  const noAccessCharacteristics = myCharacteristics === UserPermissionType.NO_ACCESS;
  const isCharacteristicsViewer = myCharacteristics === UserPermissionType.VIEWER;

  const isAdmin = role?.name === RoleName.Admin || role?.name === RoleName.Analyst;
  // Editing needs admin/analyst AND not a characteristics viewer; viewers still get a read-only popover.
  const canEdit = isAdmin && !isCharacteristicsViewer;
  const systemCharacteristics = useMemo(() => characteristics.filter((item) => item.is_system), [characteristics]);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const clientName = useClientSelector()?.name ?? "";

  // Split the flat list into up to 3 columns, mirroring the deal CharacteristicsList layout.
  const columns = useMemo(() => {
    const perColumn = Math.ceil(characteristics.length / 3);
    if (perColumn === 0) return [];
    return [
      characteristics.slice(0, perColumn),
      characteristics.slice(perColumn, perColumn * 2),
      characteristics.slice(perColumn * 2)
    ].filter((column) => column.length > 0);
  }, [characteristics]);

  // No access to characteristics — hide the bar entirely (deal hides each characteristic pill; here the
  // whole bar is characteristics).
  if (noAccessCharacteristics) return null;

  console.log(characteristics);

  return (
    <>
      <Box
        sx={{
          borderRadius: "8px",
          bgcolor: "white",
          boxShadow: "0 1px 2px 0 #1212171a",
          transition: "0.5s",
          width: "100%"
        }}
      >
        <Stack direction="row" justifyContent="space-between" sx={{ padding: "16px" }}>
          <Stack direction="row" gap="10px" alignItems="center" flexWrap="wrap">
            <IconButton onClick={() => setIsPanelOpen((prev) => !prev)}>
              <KeyboardArrowRightIcon sx={{ transform: isPanelOpen ? "rotate(90deg)" : "none", cursor: "pointer" }} />
            </IconButton>
            {systemCharacteristics.length && clientId !== undefined ? (
              systemCharacteristics.map((item) => (
                <ClientCharacteristicPill
                  key={item.id}
                  characteristic={item}
                  clientId={clientId}
                  value={displayValue(item)}
                  canEdit={canEdit}
                />
              ))
            ) : (
              <Typography sx={{ color: "#92A7BC", fontSize: "13px" }}>No characteristics.</Typography>
            )}
            {clientId !== undefined && (
              <MaterialTooltip title="Manage characteristics" isShow variant="dark" placement="top">
                <IconButton sx={{ backgroundColor: "#edf1f9" }} onClick={() => setIsDrawerOpen(true)}>
                  <ModeEditOutlineOutlinedIcon sx={{ fontSize: 20, color: "#5d7782" }} />
                </IconButton>
              </MaterialTooltip>
            )}
          </Stack>
        </Stack>

        {characteristics.length > 0 && (
          <Collapse in={isPanelOpen} timeout={500} unmountOnExit>
            <Stack sx={{ padding: "16px" }}>
              <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35" }}>
                Full Characteristics
              </Typography>
              <Stack direction="row" justifyContent="space-between" sx={{ overflowY: "auto", maxHeight: "165px" }}>
                {columns.map((column, columnIdx) => (
                  <Stack key={columnIdx} sx={{ width: "33%", padding: "0px", ul: { pl: "25px", pr: "25px" } }}>
                    <ul>
                      {column.map((item) => (
                        <li key={item.name}>
                          <Stack direction="row" justifyContent="flex-start" alignItems="center">
                            <Typography
                              sx={{
                                fontSize: "15px",
                                fontWeight: "400",
                                color: "#496682",
                                marginRight: "5px",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {item.name}:
                            </Typography>
                            <MaterialTooltip title={displayValue(item)} placement="top">
                              <Typography
                                sx={{
                                  fontSize: "15px",
                                  fontWeight: "400",
                                  color: "#071F35",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                  maxWidth: "300px"
                                }}
                              >
                                {displayValue(item)}
                              </Typography>
                            </MaterialTooltip>
                          </Stack>
                        </li>
                      ))}
                    </ul>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Collapse>
        )}
      </Box>
      {clientId !== undefined && (
        <ClientCharacteristicsDrawer
          open={isDrawerOpen}
          clientId={clientId}
          clientName={clientName}
          canEdit={canEdit}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
    </>
  );
};

export default ClientCharacteristicValues;
